import type {
  ApprovalActorQualification,
  ApprovalRequestRecord,
  ApprovalStatus,
} from '@nadoutong/contracts';

/**
 * Pure presentation helpers for the approval center (epic #1030 E1-S3,
 * #1039). No fetching, no React — shared by the page and its tests.
 */

export interface ApprovalTimelineStep {
  readonly toStatus: ApprovalStatus;
  readonly actorOpsUserId: string;
  readonly note: string | null;
  readonly at: string;
  /**
   * Present on an intermediate multi-stage advance (epic #1033 E4-C2,
   * #1126): the stage number this approval cleared. Absent on the
   * `pending` (creation) step and the terminal step.
   */
  readonly stage?: number;
  /**
   * How the actor qualified for the stage they cleared (#2265):
   * `stage_role` if they held the stage's approver role, `admin_bypass`
   * if the admin short-circuit is what let them past it. Absent on stage
   * decisions recorded before #2265 and on the creation step — absent
   * means "not recorded", never "bypass", so callers must not render it
   * as a clean bill of health.
   */
  readonly qualifiedBy?: ApprovalActorQualification;
}

/**
 * Status timeline for one request, mirroring `approval_request_transitions`
 * plus the multi-stage advances recorded in `stage_decisions`.
 *
 * Single-stage lifecycle (E1-S2): creation ⇒ the pending step, and
 * `decided_by/decided_at/decision_note` ⇒ the terminal step (including
 * `cancelled`, where they identify the canceller). Multi-stage (E4-C2):
 * each `stage_decisions` entry is woven in as an intermediate step
 * between creation and the terminal decision — an intermediate advance
 * keeps the request `pending`, so those steps render with a `pending`
 * tone and carry the `stage` they cleared. The terminal step is still
 * the row's `decided_*` fields (final-stage approve, or a rejection at
 * any stage).
 */
export function deriveApprovalTimeline(
  request: ApprovalRequestRecord,
): readonly ApprovalTimelineStep[] {
  const steps: ApprovalTimelineStep[] = [
    {
      toStatus: 'pending',
      actorOpsUserId: request.requester_ops_user_id,
      note: null,
      at: request.created_at,
    },
  ];
  for (const decision of request.stage_decisions) {
    steps.push({
      toStatus: 'pending',
      actorOpsUserId: decision.decided_by_ops_user_id,
      note: null,
      at: decision.decided_at,
      stage: decision.stage,
      ...(decision.qualified_by === undefined ? {} : { qualifiedBy: decision.qualified_by }),
    });
  }
  if (
    request.status !== 'pending' &&
    request.decided_by_ops_user_id !== null &&
    request.decided_at !== null
  ) {
    steps.push({
      toStatus: request.status,
      actorOpsUserId: request.decided_by_ops_user_id,
      note: request.decision_note,
      at: request.decided_at,
    });
  }
  return steps;
}

const SUMMARY_MAX_LENGTH = 140;

/**
 * Compact, kind-agnostic one-line payload summary for queue rows.
 * Payload shapes are owned by each kind's action site (E1-S5+), so the
 * queue renders top-level primitives generically instead of guessing
 * per-kind fields.
 */
export function summarizeApprovalPayload(payload: Record<string, unknown>): string {
  const parts: string[] = [];
  for (const [key, value] of Object.entries(payload)) {
    if (value === null || value === undefined) {
      continue;
    }
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      parts.push(`${key}=${String(value)}`);
      continue;
    }
    parts.push(`${key}=${safeJson(value)}`);
  }
  const joined = parts.join(' · ');
  if (joined.length === 0) {
    return '—';
  }
  return joined.length > SUMMARY_MAX_LENGTH
    ? `${joined.slice(0, SUMMARY_MAX_LENGTH - 1)}…`
    : joined;
}

function safeJson(value: unknown): string {
  try {
    return JSON.stringify(value) ?? '—';
  } catch {
    return '—';
  }
}

/**
 * Short waiting-age readout for queue rows: `<1m`, `Nm`, `Nh`, `Nd`.
 * Unit letters are locale-neutral; invalid timestamps render an em
 * dash instead of NaN.
 */
export function formatAgeShort(fromIso: string, nowMs: number): string {
  const fromMs = Date.parse(fromIso);
  if (!Number.isFinite(fromMs)) {
    return '—';
  }
  const deltaMs = Math.max(0, nowMs - fromMs);
  const minutes = Math.floor(deltaMs / 60_000);
  if (minutes < 1) {
    return '<1m';
  }
  if (minutes < 60) {
    return `${minutes}m`;
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours}h`;
  }
  return `${Math.floor(hours / 24)}d`;
}
